// ============================================================================
// MÓDULO: POLÍTICA DE PRIVACIDADE (LGPD)
// ARQUIVO: src/components/PrivacyPolicy.jsx
// OBJETIVO: Página dedicada de compliance LGPD — complementa (não substitui)
// o resumo já existente no rodapé (Footer, LegalDesignUI.jsx).
// ============================================================================

import React from 'react';
import { LAW_FIRM_INFO } from '../config/Config';

const Section = ({ title, children }) => (
  <section className="mb-10">
    <h2 className="text-xl font-serif font-bold text-[#20243B] mb-3 border-l-4 border-[#C5A059] pl-3">
      {title}
    </h2>
    <div className="text-gray-700 text-sm leading-relaxed space-y-3">
      {children}
    </div>
  </section>
);

export const PrivacyPolicy = () => {
  const lastUpdated = '04/09/2026';

  return (
    <div className="py-16 md:py-20 bg-white min-h-screen">
      <div className="container mx-auto px-6 max-w-3xl">
        <div className="mb-12 text-center">
          <span className="inline-block px-4 py-1 rounded-full border border-[#C5A059] text-[#C5A059] text-[10px] font-bold uppercase tracking-[0.3em] mb-4">
            Compliance LGPD
          </span>
          <h1 className="text-3xl md:text-4xl font-serif font-bold text-[#20243B] mb-3">
            Política de Privacidade e Proteção de Dados
          </h1>
          <p className="text-gray-500 text-xs">Última atualização: {lastUpdated}</p>
        </div>

        <Section title="1. Quem trata os seus dados (Controlador)">
          <p>
            <strong>{LAW_FIRM_INFO.name}</strong>, advogado inscrito na {LAW_FIRM_INFO.oab},
            com endereço profissional em {LAW_FIRM_INFO.address}, é o controlador dos dados
            pessoais tratados por meio deste site, nos termos da Lei nº 13.709/2018 (LGPD).
          </p>
        </Section>

        <Section title="2. Encarregado de Proteção de Dados (DPO)">
          <p>
            Nesta banca de advocacia unipessoal, o próprio advogado {LAW_FIRM_INFO.name} atua
            como Encarregado de Proteção de Dados. Solicitações relativas a dados pessoais
            devem ser dirigidas a{' '}
            <a href={`mailto:${LAW_FIRM_INFO.contact.email}`} className="text-[#20243B] font-semibold underline">
              {LAW_FIRM_INFO.contact.email}
            </a>.
          </p>
        </Section>

        <Section title="3. Quais dados são coletados">
          <ul className="list-disc pl-5 space-y-2">
            <li>Dados de identificação e contato fornecidos voluntariamente (nome, e-mail, telefone), ao preencher formulário de contato/novo caso ou ao falar pelo WhatsApp/e-mail.</li>
            <li>Documentos e informações do caso, quando o visitante se torna cliente e inicia uma relação advogado-cliente formal.</li>
            <li>Dados de navegação (cookies essenciais e, mediante consentimento, cookies analíticos) — ver o banner de cookies exibido no site.</li>
          </ul>
        </Section>

        <Section title="4. Para que os dados são usados (finalidade)">
          <ul className="list-disc pl-5 space-y-2">
            <li>Responder a contatos e realizar o atendimento pré-contratual (diagnóstico jurídico inicial).</li>
            <li>Prestar o serviço advocatício contratado, incluindo comunicação sobre o andamento do caso.</li>
            <li>Cumprir obrigações legais e regulatórias aplicáveis à advocacia (Estatuto da Advocacia e da OAB, normas fiscais).</li>
            <li>Garantir a segurança e o funcionamento técnico do site e do Portal do Cliente.</li>
          </ul>
        </Section>

        <Section title="5. Base legal do tratamento (Art. 7º da LGPD)">
          <p>
            O tratamento se apoia, conforme o caso: no <strong>consentimento</strong> do titular
            (ex.: cookies analíticos), na <strong>execução de procedimentos preliminares
            e do contrato</strong> de prestação de serviços advocatícios, no{' '}
            <strong>cumprimento de obrigação legal ou regulatória</strong> e no{' '}
            <strong>exercício regular de direitos</strong> em processo judicial, administrativo
            ou arbitral, quando aplicável.
          </p>
        </Section>

        <Section title="6. Compartilhamento com terceiros">
          <p>
            Não vendemos dados pessoais. A infraestrutura técnica do site e do Portal do
            Cliente utiliza serviços de nuvem de terceiros (Google Firebase/Google Cloud
            Platform) como operadores de dados, sob controle de acesso restrito e criptografia
            em trânsito (TLS). O sigilo profissional advogado-cliente (Art. 7º, XIX, e Art. 34,
            VII, do Estatuto da Advocacia) é preservado em qualquer hipótese de compartilhamento
            de dados do processo.
          </p>
        </Section>

        <Section title="7. Por quanto tempo os dados são guardados">
          <p>
            Os dados são mantidos pelo prazo necessário ao cumprimento da finalidade para a
            qual foram coletados e, quando vinculados a um caso, pelo prazo de guarda exigido
            pelas obrigações legais e prescricionais aplicáveis à atividade advocatícia.
          </p>
        </Section>

        <Section title="8. Seus direitos como titular de dados (Art. 18 da LGPD)">
          <p>Você pode solicitar, a qualquer momento, mediante contato com o Encarregado (item 2):</p>
          <ul className="list-disc pl-5 space-y-2">
            <li>Confirmação da existência de tratamento e acesso aos seus dados;</li>
            <li>Correção de dados incompletos, inexatos ou desatualizados;</li>
            <li>Anonimização, bloqueio ou eliminação de dados desnecessários ou tratados em desconformidade com a lei;</li>
            <li>Portabilidade dos dados a outro fornecedor de serviço;</li>
            <li>Informação sobre com quem os dados foram compartilhados;</li>
            <li>Revogação do consentimento, quando o tratamento se basear nele;</li>
            <li>Oposição a tratamento realizado com base em hipótese legal diversa do consentimento.</li>
          </ul>
        </Section>

        <Section title="9. Cookies">
          <p>
            O site utiliza cookies essenciais (necessários para login e segurança) e, mediante
            sua autorização explícita, cookies analíticos anônimos. Você pode gerenciar essa
            escolha a qualquer momento pelo banner de cookies exibido no site.
          </p>
        </Section>

        <Section title="10. Alterações desta política">
          <p>
            Esta política pode ser atualizada para refletir mudanças legais ou operacionais.
            A data da última atualização está indicada no topo desta página.
          </p>
        </Section>

        <div className="mt-12 pt-8 border-t border-gray-200 text-center text-xs text-gray-400">
          Dúvidas sobre seus dados pessoais? Escreva para{' '}
          <a href={`mailto:${LAW_FIRM_INFO.contact.email}`} className="text-[#20243B] font-semibold underline">
            {LAW_FIRM_INFO.contact.email}
          </a>.
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
